import { EntityContent } from 'src/content/entity-content.js';
import '../../library/fields/index.js';
import '../../library/fields/d-view-html';
import '../../library/editors/index.js';
import '../../library/editors/components/d-edit-classification.js';
import '../../library/editors/components/d-select-add';
import '../../library/lists/d-list-section-attachment.js';
import '../../library/lists/d-list-section.js';
import '../../library/components/d-signatures-section';
import '../../library/components/d-view-attachments';
import { customElement } from 'lit/decorators.js';
import type { TemplateResult } from 'lit';
import { html } from 'lit';
import { toSelectOptionsEmployees, toSelectOptionsPartners } from 'src/models/content/build-contract-view';
import { isEmptyHtmlContent, isEmptyOrInvalidString } from 'src/utilities/text';
import { LocalDate } from 'src/utilities/local-date';
import { t } from 'src/i18n';
import type { UpdateSectionItem } from '../update-section-item';
import type { ContractViewEditItem } from './contract-view-edit-item';
import type { ContractView } from './contract-view';
import type { ListDefinition } from '../list-definition';
import type { SectionField } from '../../library/components/section-field';

/**
 *
 * STATUS OK
 */
@customElement('d-contract-view')
export class DContractView extends EntityContent<ContractView, ContractViewEditItem> {
  // Avtaler bør gjennomgås i god tid før de utløper: én måned før «gyldig til»,
  // ikke én uke som er standard for øvrige dokumenttyper.
  protected planReviewDefaultDate(): string {
    if (this.entityView.validToDate) {
      return LocalDate.fromString(this.entityView.validToDate).plusMonths(-1).toString();
    }
    return super.planReviewDefaultDate();
  }

  private get signingDisabled() {
    return (
      (!this.hasValidContent && this.entityView.attachments.length === 0) ||
      (this.entityView.employees.length === 0 && this.entityView.partners.length === 0)
    );
  }

  private get unsavedTemplateContent() {
    return this.entityView.content !== '' && this.entityView.contentLastModifiedBy?.toLowerCase() === 'trinnvis-teamet';
  }

  private get hasValidContent() {
    return (
      !isEmptyHtmlContent(this.entityView.content) &&
      this.entityView.contentLastModifiedBy?.toLowerCase() !== 'trinnvis-teamet'
    );
  }

  renderViewMode() {
    return html` ${this.renderFieldSection('contracts', this.viewFields(), this.entityView)} `;
  }

  async initializeEditItem() {
    let content = this.entityView.content;
    if (this.unsavedTemplateContent) {
      content = '';
    }
    this.editItem = {
      accessControl: this.entityView.accessControl,
      classification: this.entityView.classification,
      content: content,
      employees: this.entityView.employees,
      partners: this.entityView.partners,
      validFromDate: this.entityView.validFromDate,
      validToDate: this.entityView.validToDate,
      name: this.entityView.name,
      pages: this.entityView.pages,
    };
  }

  doneDisabled(): boolean {
    return isEmptyOrInvalidString(this.editItem?.name);
  }

  fieldActionInsertTemplateContent() {
    if (this.editItem) {
      this.editItem = {
        ...this.editItem,
        content: this.entityView.content,
      };
    }
  }

  renderEditItem(item: ContractViewEditItem): TemplateResult<1> {
    return html`
      ${this.renderEditClassification(item)} ${this.renderFieldSection('contracts', this.editFields(item), item)}
      ${this.renderViewAttachments()}
    `;
  }

  renderViewAttachments() {
    return html`
      <d-view-attachments
        .writeAccess=${this.entityView.currentUserHasWriteAccess}
        field="contracts_documents"
        .items=${this.entityView.attachments}
        @upload-file=${this.onUploadFile}
      ></d-view-attachments>
    `;
  }

  protected viewFields(): SectionField[] {
    return [
      {
        field: 'classificationText',
        fieldForLabel: 'generalFields_access',
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.classificationText !== '',
      },
      {
        field: 'validFromDate',
        type: 'view-date',
        format: 'date-short',
        class: 'minWidth300',
      },
      {
        field: 'validToDate',
        type: 'view-date',
        format: 'date-short',
        class: 'minWidth300',
      },
      {
        field: 'contractPartyNames',
        type: 'view-text',
        class: 'fullWidth',
      },
      {
        type: 'custom',
        render: () => this.renderSigningSection(),
        condition: !this.entityView.deleted,
      },
      {
        field: 'content',
        type: 'view-html',
      },
    ];
  }

  protected lists(): ListDefinition[] {
    return [
      {
        field: 'contracts_documents',
        type: 'custom',
        icon: 'attachments',
        items: 'attachments',
        render: () => this.renderViewAttachments(),
      },
    ];
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return this.entityView;
  }

  private renderSigningSection() {
    return html`<d-signatures-section
      .entityView=${this.entityView}
      .signingDisabled=${this.signingDisabled}
    ></d-signatures-section>`;
  }

  private insertTemplateContentAction(content: string): string {
    if (isEmptyHtmlContent(content) && this.unsavedTemplateContent) {
      return t('contracts.insertTemplateContent');
    }
    return '';
  }

  private editFields(item: ContractViewEditItem): SectionField[] {
    return [
      {
        field: 'name',
        type: 'edit-text',
        placeholder: t('reports.fieldMustBeFilledOut'),
        autofocus: !this.entityView.isConfirmedEntity,
        markIfEmpty: true,
        class: 'fullWidth',
      },
      {
        field: 'validFromDate',
        type: 'edit-date',
        class: 'minWidth300',
      },
      {
        field: 'validToDate',
        type: 'edit-date',
        class: 'minWidth300',
      },
      {
        field: 'contractPartyNames',
        value: () => {
          return this.entityView.contractPartyNames;
        },
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.relationsLocked,
      },
      {
        field: 'employees',
        type: 'edit-select-add',
        allowRemoveAll: true,
        placeholder: t('reports.selectPerson'),
        options: toSelectOptionsEmployees(this.entityView.availableEmployees),
        condition: !this.entityView.relationsLocked,
        class: 'fullWidth',
      },
      {
        field: 'partners',
        type: 'edit-select-add',
        allowRemoveAll: true,
        placeholder: t('contracts.selectPartner'),
        options: toSelectOptionsPartners(this.entityView.availablePartners),
        condition: !this.entityView.relationsLocked,
        class: 'fullWidth',
      },
      {
        field: 'content',
        type: 'edit-html',
        docsForLinking: this.entityView.docsForLinking,
        labelAction: {
          name: this.insertTemplateContentAction(item.content),
          action: 'fieldActionInsertTemplateContent',
        },
      },
    ];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-contract-view': DContractView;
  }
}
